'use client';

import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { AgentLineChat } from '@/app/components/icons/AgentLineChat';
import { AgentLineFlie } from '@/app/components/icons/AgentLineFlie';
import { ProductLineAmount } from '@/app/components/icons/ProductLineAmount';
import { isClawProvisioning } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { usePathname } from '@/i18n/routing';
import { useSubscribeClawSessionsState } from '../../_hook/use-subscribe-claw-sessions-state';
import { ChannelConfigButton } from '../ChannelConfig/ChannelConfigButton';
import { NavButton, type NavItem } from './NavButton';
import { NavGithubCard } from './NavGithubCard';
import { NavHeader } from './NavHeader';
import { NavSessionList } from './NavSessionList';
import { NavUserCard } from './NavUserCard';
import { Search } from './Search/Search';

const Divider = () => <div className="h-px w-full bg-system-white12 light:bg-system-white08" />;

export const Nav = ({ onToggleCollapse }: { onToggleCollapse: () => void }) => {
  const t = useTranslations();
  const routerPathname = usePathname();

  const { userInfo, activeClaw, clawStatus, activeSessionId, setActiveSessionId } = useStore((state) => ({
    userInfo: state.userInfo,
    activeClaw: state.activeClaw,
    clawStatus: state.clawStatus,
    activeSessionId: state.activeSessionId,
    setActiveSessionId: state.setActiveSessionId,
  }));

  const isClawCreating = isClawProvisioning(clawStatus);
  useSubscribeClawSessionsState(clawStatus === 'CLAW_STATUS_ACTIVE' ? activeClaw?.id : null);

  const isClawProvisioned = clawStatus === 'CLAW_STATUS_ACTIVE' || clawStatus === 'CLAW_STATUS_EXPIRED';
  const pathname =
    routerPathname === '/claw' ? (activeSessionId ? `/claw/${activeSessionId}` : '/claw') : routerPathname;

  const chatItem = useMemo<NavItem>(
    () => ({
      id: 'session',
      type: 'session',
      path: '/claw',
      icon: AgentLineChat,
      label: t('claw.nav.chat'),
    }),
    [],
  );

  const visibleFeatureItems = useMemo<NavItem[]>(() => {
    const items: NavItem[] = [
      {
        id: 'cases',
        type: 'cases',
        path: '/claw/cases',
        icon: ProductLineAmount,
        label: t('claw.nav.cases'),
        requiresActiveClaw: false,
        elementName: 'quality_case',
      },
      {
        id: 'file',
        type: 'file',
        path: '/claw/file',
        icon: AgentLineFlie,
        label: t('claw.nav.files'),
        elementName: 'folder',
      },
    ];
    return items.filter((item) => isClawProvisioned || item.requiresActiveClaw === false);
  }, [isClawProvisioned]);

  return (
    <aside className="flex h-screen w-[264px] flex-col gap-4 light:border-system-white08 light:border-r bg-transparent light:bg-[#F4F4FA] px-2 py-4 text-system-white">
      <NavHeader onToggleCollapse={onToggleCollapse} />

      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <NavButton
          item={chatItem}
          pathname={pathname}
          activeSessionId={activeSessionId}
          onBeforeNavigate={() => setActiveSessionId(null)}
        />

        <Divider />

        {(activeClaw || isClawCreating) && (
          <>
            <Search />
            <NavSessionList pathname={pathname} activeSessionId={activeSessionId} isClawCreating={isClawCreating} />
            <Divider />
          </>
        )}

        {visibleFeatureItems.length > 0 && (
          <div className="flex flex-col gap-2">
            {visibleFeatureItems.map((item) => (
              <NavButton key={item.id} item={item} pathname={pathname} activeSessionId={activeSessionId} />
            ))}
          </div>
        )}
      </div>

      {clawStatus === 'CLAW_STATUS_ACTIVE' && activeClaw?.id && (
        <div className="px-2">
          <ChannelConfigButton clawId={activeClaw.id} variant="nav" />
        </div>
      )}

      <NavGithubCard />

      {userInfo && <NavUserCard userInfo={userInfo} />}
    </aside>
  );
};
